import type { SupervisorContact } from '@saniplus/contracts';
import {
  Button,
  ChevronRight,
  Notice,
  Phone,
  Sheet,
  User,
  decorativeIcon,
} from '@saniplus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * THE PEOPLE BEHIND THE SCREEN — quiet on a working screen, all of them.
 *
 * Two defects in the shipped version, both about rank.
 *
 * **It out-ranked the punch.** « Contacter mon superviseur » rendered as a
 * filled `--color-action` block: the same fill, the same weight, the same
 * ~64 px as the punch key, sitting directly under it. Two primaries on one
 * surface is a product-wide refusal, and on the one screen this app exists
 * for it told a worker whose shift was running normally that somebody needed
 * phoning. A supervisor's name and number are *information*, and information
 * does not get the action colour. So: a quiet full-width line under the key,
 * `--color-link` on the night canvas, no fill, no bevel — visibly subordinate
 * to the key and still a 48 px target in gloves.
 *
 * **It showed one supervisor of up to five.** `GET /me/context` returns the
 * supervisors whose membership actually covers *this screen's site* — the
 * server does that scoping in the `where` clause, so a worker at Pickering is
 * never handed the number of a supervisor scoped to Delta — deterministically
 * ordered and capped at five. The screen rendered `contacts[0]` and dropped
 * the rest, which at 02:00 is the difference between reaching somebody and
 * reaching somebody's voicemail. Every contact is now listed, and the sheet
 * names the site so a worker who moves between plants can see which crew's
 * supervisors these are.
 *
 * A contact with `phone: null` is listed and is **not** a link. A dead
 * `tel:` on a dead-end screen is the product lying at the exact moment a
 * worker needs to believe it.
 *
 * This is the *working-screen* rendering. On a screen with no way forward —
 * a stale open punch, a closed window, no shift — the supervisor IS the
 * primary action, and `SupervisorAction` still draws it as the key.
 */
interface SupervisorSheetProps {
  readonly contacts: readonly SupervisorContact[] | undefined;
  /** The site these supervisors cover — the current shift's or the open session's. */
  readonly siteName?: string | undefined;
  readonly className?: string;
}

export function SupervisorSheet({ contacts, siteName, className }: SupervisorSheetProps) {
  const { t } = useTranslation('punch');
  const [open, setOpen] = useState(false);
  const list = contacts ?? [];

  return (
    <>
      <Button
        variant="quiet"
        size="lg"
        fullWidth
        iconEnd={<ChevronRight {...decorativeIcon('sm')} />}
        onClick={() => setOpen(true)}
        data-testid="supervisor-line"
        {...(className === undefined ? {} : { className })}
      >
        {t('supervisor.contact')}
      </Button>

      <Sheet
        open={open}
        onOpenChange={setOpen}
        title={t('supervisor.panel')}
        {...(siteName === undefined ? {} : { description: siteName })}
        closeLabel={t('action.close')}
      >
        {list.length === 0 ? (
          <Notice tone="neutral" title={t('supervisor.contact')} description={t('supervisor.none')} />
        ) : (
          <ul className="flex flex-col">
            {list.map((contact) => (
              <li
                key={contact.userId}
                className="flex items-center gap-3 border-t border-hairline py-3 first:border-t-0 first:pt-0"
              >
                <span aria-hidden="true" className="shrink-0 text-ink-muted">
                  <User {...decorativeIcon('xl')} />
                </span>
                <div className="min-w-0 flex-1">
                  {/*
                    * A person's name is prose, not a fact — proportional Sans
                    * is easier to read at arm's length and a monospaced name
                    * looks like a serial number. The NUMBER is mono, because
                    * it is a number somebody reads aloud into a handset.
                    */}
                  <p className="text-subject text-ink">
                    {contact.firstName} {contact.lastName}
                  </p>
                  <p className="mt-0.5 text-body text-ink-muted">
                    {contact.phone === null ? (
                      t('supervisor.noPhone')
                    ) : (
                      <span className="num">{contact.phone}</span>
                    )}
                  </p>
                </div>
                {contact.phone === null ? null : (
                  <Button
                    asChild
                    variant="primary"
                    size="lg"
                    iconStart={<Phone {...decorativeIcon('sm')} />}
                    className="shrink-0"
                  >
                    <a
                      href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}
                      aria-label={t('supervisor.callName', {
                        name: `${contact.firstName} ${contact.lastName}`,
                      })}
                    >
                      {t('supervisor.call')}
                    </a>
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Sheet>
    </>
  );
}
